import { axios } from "@/libraries/axios";
import { IMAGE_MIME_TYPE } from "@/libraries/constant";
import { isFile, isFiles, randFileName } from "@/libraries/helper";
import type {
	CreateGroupSchema,
	SyncGroupParamsSchema,
	UpdateGroupSchema,
} from "@/schemas";
import type { GetGroupsParamSchema } from "@/schemas/group";
import type {
	CreateGroupResponse,
	GetGroupResponse,
	GetGroupsResponse,
	GetSyncGroupResponse,
	Group,
	UpdateGroupResponse,
} from "@/types";
import { fileService } from "./file";

class GroupService {
	get defaultListParams(): GetGroupsParamSchema {
		return {
			page: 1,
			limit: 25,
		};
	}
	async list(params?: GetGroupsParamSchema) {
		try {
			const res = await axios.get<GetGroupsResponse>("/api/v1/groups", {
				params: {
					...this.defaultListParams,
					...params,
				},
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async get(id: any) {
		try {
			const res = await axios.get<GetGroupResponse>(`/api/v1/groups/${id}`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async create(payload: CreateGroupSchema) {
		try {
			const { avatar, ...group } = payload;
			const isAvatarFile = isFile(avatar);
			const isAvatarFiles = isFiles(avatar);
			if (isAvatarFile || isAvatarFiles) {
				const resPresign = await fileService.avatarPresign({
					file_name:
						avatar instanceof File
							? avatar.name
							: avatar instanceof FileList && avatar.length
								? avatar[0].name
								: randFileName(
										fileService.getExtensionFromMimeType(IMAGE_MIME_TYPE.JPG),
									),
					file:
						avatar instanceof File
							? avatar
							: avatar instanceof FileList
								? avatar[0]
								: undefined,
				});
				if (resPresign.fields) {
					// @ts-expect-error
					group.file_id = resPresign.fields.key;
				}
			}
			const res = await axios.post<CreateGroupResponse>("/api/v1/groups", {
				group,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async update(payload: UpdateGroupSchema) {
		try {
			const { id, avatar, ...group } = payload;
			if (isFile(avatar) || isFiles(avatar)) {
				const resPresign = await fileService.avatarPresign({
					file_name:
						isFiles(avatar) || isFile(avatar)
							? // @ts-expect-error
								(avatar.name ?? avatar?.[0]?.name)
							: randFileName(
									fileService.getExtensionFromMimeType(IMAGE_MIME_TYPE.JPG),
								),
					file: isFiles(avatar) ? avatar?.[0] : avatar,
				});
				if (resPresign.fields) {
					// @ts-expect-error
					group.file_id = resPresign.fields.key;
				}
			}
			const res = await axios.patch<UpdateGroupResponse>(
				`/api/v1/groups/${id}`,
				{
					group,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async sync(params: SyncGroupParamsSchema) {
		try {
			const res = await axios.get<GetSyncGroupResponse>("/api/v1/groups/sync", {
				params,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async close(id: any) {
		try {
			const res = await axios.get(`/api/v1/groups/${id}/closed`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async left(id: Group["id"]) {
		try {
			const res = await axios.get(`/api/v1/groups/${id}/left`);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}

export const groupService = Object.freeze(new GroupService());
